import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ApiError } from '../lib/api';
import { Button } from './ui/button';

/**
 * Error Boundary for read screens (frontend rule: useSuspenseQuery reads MUST be
 * wrapped in an Error Boundary). Catches render/query errors thrown by suspended
 * children and shows a retryable fallback instead of a blank screen.
 *
 * `resetKey` lets callers reset the boundary when navigation params change
 * (e.g. switching category / opening a different post), so a previous error
 * doesn't stick to a now-valid view.
 */
interface Props {
  children: ReactNode;
  /** Change this to clear a captured error (e.g. route param / category slug). */
  resetKey?: unknown;
  /** Optional custom fallback renderer. */
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props) {
    // Clearing on resetKey change lets the user move to another category/post
    // and re-attempt the query rather than staying stuck on the old error.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep a console trace for debugging; no PII is logged.
    console.error('Read screen error:', error, info.componentStack);
  }

  private retry = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.retry);

    return (
      <div role="alert" className="px-4 py-8 text-center">
        <p className="text-sm text-text-muted">{describe(error)}</p>
        <Button variant="secondary" className="mt-4" onClick={this.retry}>
          다시 시도
        </Button>
      </div>
    );
  }
}

function describe(error: Error): string {
  if (error instanceof ApiError && error.status === 404) {
    return '요청한 내용을 찾을 수 없어요.';
  }
  return '내용을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.';
}
